import Class from '@/Class';
import ComponentManager from '@/Management/ComponentManager';
import Factory from '@/Factories/Factory';
import { OrientationE } from '@/Enums/OrientationE';
import { FlowLayoutModeE } from '@/Enums/FlowLayoutModeE';
import Utilities from '@/Utility/Utilities';
import { IDetailViewDTO, IDetailViewGroupDTO, IDVGItemDTO } from "coreframetypings/dto";
import { FactoryParameter, FactoryParameterParameter } from '#/CoreFrameInterfaces';
import ControlFactoryTuple from '@/Factories/ControlFactoryTuple';
import GroupFactoryTuple from '@/Factories/GroupFactoryTuple';
import ControlFactory from '@/Factories/ControlFactory';
import GroupFactory from '@/Factories/GroupFactory';
import GlobalConfiguration from '@/Management/GlobalConfiguration';

type FactoryTuple = ControlFactoryTuple | GroupFactoryTuple;

export default class LayoutRenderer extends Class {

    private static readonly CSS_CLASS_CONTROL = 'c-control';

    public constructor(private params: any) {
        super();
    }

    public static renderLayout(layoutUIGeneratorQuery: string, tuples: any[], labelColumnWidth?: KnockoutObservable<any>): JQuery {
        let layout = Factory.parseUIGeneratorQuery(layoutUIGeneratorQuery);
        const isTwoColumnLayout = layout.uiGenerator.indexOf('TwoColumn') > -1;

        if (layout.uiGenerator.indexOf('AutoTwoColumn') > -1) {
            LayoutRenderer.assignNeighbors(tuples);
            return LayoutRenderer.renderTwoColumnLayout(tuples, labelColumnWidth);
        }
        else if (isTwoColumnLayout)
            return LayoutRenderer.renderTwoColumnLayout(tuples, labelColumnWidth);
        else if (layout.uiGenerator.indexOf('OneColumn') > -1)
            return LayoutRenderer.renderOneColumnLayout(tuples, labelColumnWidth);
        else if (layout.uiGenerator.indexOf('Flow') > -1)
            return LayoutRenderer.renderFlowLayout(tuples, labelColumnWidth, layout.parameters);
        else (layout.uiGenerator.indexOf('None') > -1)
            return LayoutRenderer.renderNoneLayout(tuples, labelColumnWidth);
    }

    public static assignNeighbors(tuples: any[]): void {
        const numTuples = tuples.length;

        if (numTuples < 2)
            return;

        for (let i = 0; i < numTuples - 1; i += 2) {
            const currentElement = tuples[i];
            const neighbor = tuples[i + 1];

            neighbor.factory.neighborOf = LayoutRenderer.getDVItemName(currentElement.factory.detailViewItem.name);
        }
    }

    public static renderOneColumnLayout(tuples: any[], labelColumnWidth?: KnockoutObservable<any>): JQuery {
        return LayoutRenderer.renderColumnLayout(1, tuples, labelColumnWidth);
    }

    public static renderTwoColumnLayout(tuples: any[], labelColumnWidth?: KnockoutObservable<any>): JQuery {
        return LayoutRenderer.renderColumnLayout(2, tuples, labelColumnWidth);
    }

    public static renderColumnLayout(columnsPerRow: number, tuples: FactoryTuple[], labelColumnWidth?: KnockoutObservable<any>): JQuery {
        let rowTupleMapping: Record<string, FactoryTuple[]> = {};

        LayoutRenderer.proofNeighborExist(tuples);
        LayoutRenderer.rebuildNeighborMapping(columnsPerRow, tuples, rowTupleMapping);

        let columnsPerRowInWords: string;
        switch (columnsPerRow) {
            case 2: columnsPerRowInWords = 'Two'; break;
            case 1:
            default: columnsPerRowInWords = 'One'; break;
        }

        const control = LayoutRenderer.createLayoutContainer(true);
        LayoutRenderer.appendClassToNode(control, 'c-' + columnsPerRowInWords.toLowerCase() + 'columnlayout');

        const layout = LayoutRenderer.createLayoutContainer(true);
        LayoutRenderer.appendClassToNode(layout, 'c-columnlayout');
        LayoutRenderer.appendClassToNode(layout, 'c-columns-' + columnsPerRow);

        control.appendChild(layout);

        for (const row in rowTupleMapping) {
            const rowTuples = rowTupleMapping[row];
            const rowDiv = LayoutRenderer.createLayoutContainer(true);
            LayoutRenderer.appendClassToNode(rowDiv, 'c-columnlayoutrow');

            layout.appendChild(rowDiv);

            const rowVisibility = ko.computed(() => {
                return _.any(rowTuples, t => t.factory?.isVisible());
            });
            this.bindVisibility(rowDiv, rowVisibility);
            ko.utils.domNodeDisposal.addDisposeCallback(rowDiv, () => {
                rowVisibility.dispose();
            });

            rowTuples.forEach((t) => {
                if (!t || !t.factory)
                    return;

                if (columnsPerRow == 1 && t.factory.colspan.toLowerCase() == "two") {
                    t.factory.colspan = "one";
                }
                const item = LayoutRenderer.renderTuple(t, "column", labelColumnWidth);
                if (item)
                    rowDiv.appendChild(item);
            });
        }

        return $(control);
    }

    public static renderFlowLayout(tuples: any[], labelColumnWidth: KnockoutObservable<any>, parameter: {
        mode?: FlowLayoutModeE;
        orientation?: OrientationE;
    }): JQuery {

        let orientation = parameter && parameter.orientation ? parameter.orientation : OrientationE.Horizontal;
        let mode = parameter && parameter.mode ? parameter.mode : FlowLayoutModeE.Default;

        if (mode == FlowLayoutModeE.Default && orientation == OrientationE.Horizontal) {
            return LayoutRenderer.renderFlowLayoutDefaultMode(tuples, labelColumnWidth, orientation);
        }

        // Fallback auf twocolumnLayout
        return LayoutRenderer.renderColumnLayout(2, tuples, labelColumnWidth);
    }

    private static renderFlowLayoutColumnBasedMode(tuples: any[], labelColumnWidth: KnockoutObservable<any>, orientation: OrientationE): JQuery {
        if (orientation == OrientationE.Horizontal) {
            return LayoutRenderer.renderFlowlayoutColumnBasedAndHorizontal(tuples, labelColumnWidth);
        }
        else {
            return LayoutRenderer.renderFlowlayoutColumnBasedAndVertical(tuples, labelColumnWidth);
        }
    }

    private static renderFlowlayoutColumnBasedAndHorizontal(tuples: any[], labelColumnWidth: KnockoutObservable<any>): JQuery {
        return null;
    }

    private static renderFlowlayoutColumnBasedAndVertical(tuples: any[], labelColumnWidth: KnockoutObservable<any>): JQuery {
        return null;
    }

    private static renderFlowLayoutDefaultMode(tuples: any[], labelColumnWidth: KnockoutObservable<any>, orientation: OrientationE): JQuery {
        const wrapper = LayoutRenderer.createLayoutContainer(false);
        LayoutRenderer.appendClassToNode(wrapper, 'c-flowlayout-wrapper');

        const flowLayout = LayoutRenderer.createLayoutContainer(true);
        LayoutRenderer.appendClassToNode(flowLayout, 'c-flowlayout');

        const clear = LayoutRenderer.createLayoutContainer(false);
        LayoutRenderer.appendClassToNode(clear, 'clear');

        tuples.forEach((t) => {
            let tuple = LayoutRenderer.renderTuple(t, "flow");
            flowLayout.appendChild(tuple);
        });

        flowLayout.appendChild(clear.cloneNode());

        wrapper.appendChild(flowLayout);
        wrapper.append(clear.cloneNode());

        return $(wrapper);
    }

    private static renderTuple(tuple: FactoryTuple, layout: string, labelColumnWidth?: KnockoutObservable<any>): HTMLDivElement {
        const { factory, control: $control } = tuple;

        if (!factory)
            return null;

        const isColumnLayout = layout == "column";
        const isExpanderGroup = factory.type == 'ExpanderGroupFactory';

        // Labelposition
        let labelPosition = this.isControlFactory(factory) ? factory.labelPosition.toLowerCase() : '';

        const labelPositionClass = labelPosition != '' ? 'c-labelposition-' + labelPosition : '';
        const labelAlignmentClass = labelPosition != '' ? 'c-alignment-' + labelPosition : '';

        const item = LayoutRenderer.createLayoutContainer(true);
        LayoutRenderer.appendClassToNode(item, isColumnLayout ? 'c-columnlayoutitem' : 'c-flowlayoutitem');

        if (isColumnLayout) {
            LayoutRenderer.appendClassToNode(item, labelPositionClass);
            LayoutRenderer.appendClassToNode(item, 'c-colspan-' + factory.colspan.toLowerCase());
        }

        this.bindVisibility(item, factory.isVisible);

        // Label
        if (this.isControlFactory(factory) && !isExpanderGroup) {
            const labelWrapper = LayoutRenderer.createLayoutContainer(false);
            LayoutRenderer.appendClassToNode(labelWrapper, 'c-caption');
            LayoutRenderer.appendClassToNode(labelWrapper, isColumnLayout ? labelAlignmentClass : labelPositionClass);
            LayoutRenderer.appendClassToNode(labelWrapper, 'c-labelcolumn');

            if (labelColumnWidth) {
                this.bindLabelColumnWidth(labelWrapper, labelColumnWidth);
            }

            const label = factory.label.getControl()[0];
            if (label)
                labelWrapper.appendChild(label);

            item.appendChild(labelWrapper);
        }

        // Control
        const controlWrapper = LayoutRenderer.createLayoutContainer(true);

        const control = $control[0];
        if (control)
            controlWrapper.appendChild(control);

        item.appendChild(controlWrapper);

        return item;
    }

    private static createLayoutContainer(appendCssClassControl: boolean): HTMLDivElement {
        const container = document.createElement('div');
        if (appendCssClassControl)
            container.classList.add(LayoutRenderer.CSS_CLASS_CONTROL);
        return container
    }

    private static appendClassToNode(node: HTMLElement, className: string): void {
        if (Utilities.isNullOrUndefinedOrBlank(className))
            return;

        node.classList.add(className);
    }

    private static bindVisibility(element: HTMLDivElement, isVisible: KnockoutObservable<boolean>) {
        this.bindWithDisposing(element, isVisible, value => {
            element.style.display = value ? '' : 'none';
            const hiddenClass = 'hidden';
            if (value)
                element.classList.remove(hiddenClass);
            else
                element.classList.add('hidden');
        });
    }

    private static bindLabelColumnWidth(element: HTMLDivElement, observable: KnockoutObservable<string | number>) {
        this.bindWithDisposing(element, observable, value => {
            if (typeof value  == 'number') {
               value = value.toString();
            }
            
            if (!value)
                value = '';

            element.style.flexBasis = value.length > 0 ? `${value}px` : '';
        });
    }

    private static bindWithDisposing<T>(element: HTMLDivElement, observable: KnockoutObservable<T>, handler: (value: T) => void) {
        handler(observable());

        const subscription = observable.subscribe(handler);
        ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
            subscription.dispose();
        });
    }

    private static isControlFactory(factory: any): factory is ControlFactory {
        return factory instanceof ControlFactory || (factory?.labelPosition != undefined && factory.labelPosition != '');
    }

    public static renderNoneLayout(tuples: any[], labelColumnWidth: KnockoutObservable<any>): JQuery {
        const noneLayout = LayoutRenderer.createLayoutContainer(true);
        LayoutRenderer.appendClassToNode(noneLayout, 'c-nonelayout');

        tuples.forEach((t) => {
            const $control = t?.control;
            if (!$control)
                return;

            noneLayout.append($control[0]);
        });

        return $(noneLayout);
    }

    private static proofNeighborExist(tuples: any[]): void {
        let tuplesWithNeighbor = _.filter(tuples, (tuple) => {
            return tuple.factory?.neighborOf != null;
        });

        _.each(tuplesWithNeighbor, (tupleWithNeighbor) => {
            let neighborOfTupel = _.find(tuples, (tuple) => {
                let dvItemName = LayoutRenderer.getDVItemName(tuple.factory?.detailViewItem.name);
                return dvItemName == tupleWithNeighbor.factory.neighborOf;
            });

            if (neighborOfTupel === undefined) {
                tupleWithNeighbor.factory.neighborOf = null;
            }

        });
    }

    private static rebuildNeighborMapping(columnsPerRow: number, tuples: FactoryTuple[], rowTupleMapping: Record<string, FactoryTuple[]>): void {
        let tuplesWithoutNeighbor = _.filter(tuples, (tuple) => {
            return tuple.factory instanceof GroupFactory || this.isControlFactory(tuple.factory) && tuple.factory.neighborOf == null;
        });

        let tuplesWithNeighbor = _.filter(tuples, (tuple) => {
            return this.isControlFactory(tuple.factory) && tuple.factory.neighborOf != null;
        });

        if (columnsPerRow > 1) {
            LayoutRenderer.setNeighborMappingForTuples(columnsPerRow, tuples, tuplesWithoutNeighbor, rowTupleMapping);
            LayoutRenderer.setNeighborMappingForTuples(columnsPerRow, tuples, tuplesWithNeighbor, rowTupleMapping);
        } else {
            LayoutRenderer.setNeighborMappingForTuples(columnsPerRow, tuples, tuples, rowTupleMapping);
        }
    }

    private static setNeighborMappingForTuples(columnsPerRow: number, tuples: FactoryTuple[], tuplesToSet: FactoryTuple[], rowTupleMapping: Record<string, FactoryTuple[]>): void {
        for (let i = 0; i < tuplesToSet.length; i++) {
            let tuple = tuplesToSet[i];
            let row = LayoutRenderer.findRowForTuple(columnsPerRow, tuples, tuple, rowTupleMapping);

            if (row != null) {
                let rowTuples = rowTupleMapping[row];
                if (rowTuples == null) {
                    rowTuples = [];
                    rowTupleMapping[row] = rowTuples;
                }

                if (rowTuples.length < columnsPerRow) {
                    if (columnsPerRow === 2 && rowTuples.length == 1 && this.isControlFactory(tuple.factory) && tuple.factory.neighborOf != null) {
                        let neighbor = rowTuples[0];
                        if (neighbor.factory.colspan != 'One') {
                            neighbor.factory.colspan = 'One';
                        }
                        if (tuple.factory.colspan != 'One') {
                            tuple.factory.colspan = 'One';
                        }
                    }
                    rowTuples.push(tuple);
                }
            }
        }
    }

    private static findRowForTuple(columnsPerRow: number, tuples: FactoryTuple[], tuple: FactoryTuple, rowTupleMapping: Record<string, FactoryTuple[]>): any {
        let factory = tuple.factory;
        let neighborOf: string = this.isControlFactory(factory) ? factory.neighborOf : null;

        if (neighborOf == null || columnsPerRow === 1) {
            for (let row in rowTupleMapping) {
                if (rowTupleMapping[row].indexOf(tuple) > -1) {
                    return row;
                }
            }

            return _.keys(rowTupleMapping).length;
        } else if (this.isControlFactory(factory)) {
            let neighbor = _.find(tuples, (iteratingTuple) => {
                if (iteratingTuple.factory instanceof GroupFactory)
                    return false;

                let dvItemName = LayoutRenderer.getDVItemName(iteratingTuple.factory.detailViewItem.name);
                return dvItemName === neighborOf;
            });
            //Wenn beidseitige Nachbarelemente gefunden wurden, setzt neighborOf beim TupleToSet auf null (d.h. links zuordnen) und sucht nach Row nochmal
            if (LayoutRenderer.getDVItemName(factory.detailViewItem.name) === (neighbor as ControlFactoryTuple).factory.neighborOf) {
                factory.neighborOf = null;
                return LayoutRenderer.findRowForTuple(columnsPerRow, tuples, tuple, rowTupleMapping);
            }

            if (neighbor != null) {
                return LayoutRenderer.findRowForTuple(columnsPerRow, tuples, neighbor, rowTupleMapping);
            } else {
                return null;
            }
        }
    }

    private static getDVItemName(dvItem: string): string {
        let dvItemName = dvItem;
        if (dvItemName.indexOf('.') > -1) {
            let splitted = dvItemName.split('.');

            // TODO: Möglicherweise weitere Fälle ergänzen
            if (splitted[1] === 'Value' || splitted[0] === 'ValueMLS')
                return splitted[0];
            else
                return splitted[1];
        }

        return dvItemName;
    }

    /**
     * Erzeugt ein leeres DVGItem (EmptyElementFactory) neben einem bestehenden DVGItem in einer DetailViewGroup.
     * Ist dazu gedacht, um ein DVGItem in der rechten Spalte anzeigen zu können, ohne ein richtiges Property im Modell
     * anlegen zu müssen. Manipuliert hierzu die übergebene DetailView.
     * @param detailView Die DetailView, die die DetailViewGroup samt DVGItems enthält und manipuliert werden soll.
     * @param groupName Der DetailViewGroup-Name, in welchem das DVGItem liegt, welches einen leeren Nachbarn erhalten soll.
     * @param neighborName Der Name des DVGItems, welches der Nachbar vom neuen leeren Element sein soll.
     */
    public static createEmptyNeighbor(detailView: IDetailViewDTO, groupName: string, neighborName: string): void {
        const group = this.findGroup(detailView, groupName);
        const neighborItem = this.findItem(group, neighborName);
        if (!group || !neighborItem)
            return;

        const emptyItem = this.createEmptyDVGItem(`Empty_${neighborName}_Neighbor`);
        group.items.splice(group.items.indexOf(neighborItem), 0, emptyItem);

        const neighborOfParameterName = 'NeighborOf';
        const parsedUIGenerator = Factory.parseUIGeneratorQuery(neighborItem.uiGenerator);
        if (!this.hasParameter(parsedUIGenerator, neighborOfParameterName)) {
            this.addParameter(parsedUIGenerator, neighborOfParameterName, emptyItem.name);
        }
        neighborItem.uiGenerator = Factory.toUIGeneratorQuery(parsedUIGenerator);
    }

    /**
     * Erzeugt ein leeres DVGItem (EmptyElementFactory) und fügt dieses vor einem Item in die DetailView ein.
     */
    public static insertEmptyItemBefore(detailView: IDetailViewDTO, groupName: string, itemName: string) {
        const group = this.findGroup(detailView, groupName);
        const item = this.findItem(group, itemName);
        if (!group || !item)
            return;

        const emptyItem = this.createEmptyDVGItem(`EmptyBefore_${itemName}`);
        group.items.splice(group.items.indexOf(item), 0, emptyItem);
    }

    private static findGroup(detailView: IDetailViewDTO, groupName: string): IDetailViewGroupDTO | undefined {
        if (!detailView || !detailView.detailViewGroups)
            return undefined;

        return _.find(detailView.detailViewGroups, g => g.ergName.endsWith(groupName.toUpperCase()));
    }

    private static findItem(group: IDetailViewGroupDTO, itemName: string): IDVGItemDTO | undefined {
        if (!group || !group.items)
            return undefined;

        return _.find(group.items, i => i.ergName.endsWith(itemName.toUpperCase() + '_ERGNAME'));
    }

    private static hasParameter(param: FactoryParameter, parameterName: string): boolean {
        // Der UIGenerator hat überhaupt Parameter inkl. den übergeben Parameter
        return param.parameters && parameterName in param.parameters;
    }

    private static addParameter(param: FactoryParameter, parameterName: string, value: string): void {
        if (!param.parameters)
            param.parameters = {};

        param.parameters[parameterName] = {
            value: value
        } as FactoryParameterParameter;
    }

    private static createEmptyDVGItem(name: string): IDVGItemDTO {
        return {
            uiGenerator: Factory.toUIGeneratorQuery({
                uiGenerator: 'EmptyElement',
                parameters: {
                    'visible': true
                }
            }),
            name: name,
            ergName: 'EMPTY',
            children: [],
            detailViewGroups: [],
            disableNavigation: '',
            dotNetTypeName: '',
            guiReadOnly: true,
            iconUrl: '',
            isDerived: false,
            isDerivedOverwritable: false,
            isDynamic: false,
            isMultiSelectablePDEnumeration: false,
            isPDEmbedded: false,
            isPDEmbeddedMedium: false,
            isPDEnumeration: false,
            isToManyAssoziation: false,
            length: 0,
            listView: undefined,
            possiblePDEnumerationValues: {},
            required: false,
            tooltip: '',
            unitErgName: ''
        };
    }

    public static disposeOneColumnLayout(layout: JQuery) {
        LayoutRenderer.disposeColumnLayout(layout);
    }

    public static disposeTwoColumnLayout(layout: JQuery) {
        LayoutRenderer.disposeColumnLayout(layout);
    }

    public static disposeColumnLayout(layout: JQuery) {
        layout.detach();
        ko.cleanNode(layout[0]);
        layout.remove();
    }
}

ComponentManager.setUtility('LayoutRenderer', LayoutRenderer, 'RADPresentation.HTMLClient.DefaultViewExtensions');